/**
 * Real Indian govt-job salary structures.
 * Source: 7th CPC pay matrix + actual notification PDFs (SSC, RRB, IBPS, UPSC).
 *
 * Each post: { post, post_slug, sector, level, basic, da_pct, hra_x_pct, hra_y_pct,
 *              hra_z_pct, ta_x, ta_y, ta_z, nps_pct, allowances_extra }
 * Calc: gross = basic + DA + HRA + TA + extras
 *       in_hand = gross - NPS(10% of basic+DA) - income tax (skip — varies)
 * HRA: X (metro) 24%, Y (tier-2) 16%, Z (tier-3) 8% of basic (post 2024 revision after DA reached 50%)
 * TA fixed: X=Rs. 5,400, Y=Rs. 5,400, Z=Rs. 3,600 for Level 8+; Rs. 3,600/Rs. 1,800 for lower
 *
 * Updated Mar 2026 (current DA = 53% per 7th CPC).
 */

export const CURRENT_DA_PCT = 53; // 7th CPC Dearness Allowance as of Jan 2026

export const POSTS = [
  // ---------- SSC ----------
  {
    post_slug: "ssc-cgl-inspector",
    sector: "SSC",
    post: "SSC CGL Inspector (CBIC / CGST / IB)",
    level: 7,
    basic: 44900,
    ta_lower: 3600, ta_upper: 5400,
    extras_x: 0, extras_y: 0, extras_z: 0,
    notes: "Level-7 pay matrix. Group B Gazetted post. CBIC/IB also pays special allowances post-probation.",
  },
  {
    post_slug: "ssc-cgl-aso-mea",
    sector: "SSC",
    post: "SSC CGL Assistant Section Officer (MEA / CSS / Railway Bd.)",
    level: 7,
    basic: 44900,
    ta_lower: 3600, ta_upper: 5400,
    extras_x: 5000, extras_y: 0, extras_z: 0,
    notes: "MEA-ASO posted abroad gets foreign allowance; CSS gets Delhi posting most cases.",
  },
  {
    post_slug: "ssc-cgl-auditor",
    sector: "SSC",
    post: "SSC CGL Auditor / Accountant (CAG / CGA)",
    level: 5,
    basic: 29200,
    ta_lower: 1800, ta_upper: 3600,
    extras_x: 0, extras_y: 0, extras_z: 0,
    notes: "Level-5. Promotion to Sr. Auditor in 3-4 years.",
  },
  {
    post_slug: "ssc-chsl-ldc",
    sector: "SSC",
    post: "SSC CHSL LDC / JSA",
    level: 2,
    basic: 19900,
    ta_lower: 1350, ta_upper: 3600,
    extras_x: 0, extras_y: 0, extras_z: 0,
    notes: "Level-2. Entry-level clerical post in central ministries.",
  },
  {
    post_slug: "ssc-chsl-dest",
    sector: "SSC",
    post: "SSC CHSL Data Entry Operator (DEO)",
    level: 4,
    basic: 25500,
    ta_lower: 1350, ta_upper: 3600,
    extras_x: 0, extras_y: 0, extras_z: 0,
    notes: "Level-4 in C&AG and select ministries.",
  },
  {
    post_slug: "ssc-gd-constable",
    sector: "SSC",
    post: "SSC GD Constable (BSF / CRPF / CISF / SSB / ITBP)",
    level: 3,
    basic: 21700,
    ta_lower: 1350, ta_upper: 3600,
    extras_x: 0, extras_y: 0, extras_z: 0,
    notes: "Level-3. Ration money + uniform allowance + risk allowance during field posting (Rs. 3,000-12,000).",
  },
  {
    post_slug: "ssc-je-civil",
    sector: "SSC",
    post: "SSC JE Civil (CWC / CPWD / MES / BRO)",
    level: 6,
    basic: 35400,
    ta_lower: 3600, ta_upper: 3600,
    extras_x: 0, extras_y: 0, extras_z: 0,
    notes: "Level-6. Site posting typically attracts an additional 5-8% project allowance.",
  },

  // ---------- Railway ----------
  {
    post_slug: "rrb-alp",
    sector: "Railway",
    post: "RRB Assistant Loco Pilot (ALP)",
    level: 2,
    basic: 19900,
    ta_lower: 1350, ta_upper: 3600,
    extras_x: 8500, extras_y: 8500, extras_z: 8500,
    notes: "Running allowance ~30% of basic for ALP. Senior ALP at Level-4 after 3-4 yrs.",
  },
  {
    post_slug: "rrb-je",
    sector: "Railway",
    post: "RRB Junior Engineer (JE)",
    level: 6,
    basic: 35400,
    ta_lower: 3600, ta_upper: 3600,
    extras_x: 0, extras_y: 0, extras_z: 0,
    notes: "Level-6. Tech / Mechanical / Electrical / Civil. Promotion to Senior Section Engineer in 5-6 yrs.",
  },
  {
    post_slug: "rrb-ntpc-graduate",
    sector: "Railway",
    post: "RRB NTPC Graduate (Goods Guard / SCC / CA)",
    level: 5,
    basic: 29200,
    ta_lower: 1800, ta_upper: 3600,
    extras_x: 0, extras_y: 0, extras_z: 0,
    notes: "Level-5. Commercial Apprentice and Station Master are the most popular posts in this category.",
  },
  {
    post_slug: "rrb-group-d",
    sector: "Railway",
    post: "RRB Group D (Trackman / Helper / Pointsman)",
    level: 1,
    basic: 18000,
    ta_lower: 1350, ta_upper: 3600,
    extras_x: 0, extras_y: 0, extras_z: 0,
    notes: "Level-1. Field-posting roles. Eligible for promotion to Level-2 after 2 yrs.",
  },

  // ---------- Banking ----------
  {
    post_slug: "ibps-po",
    sector: "Banking",
    post: "IBPS PO (Probationary Officer)",
    level: 0,
    basic: 36000,
    ta_lower: 0, ta_upper: 0,
    extras_x: 12000, extras_y: 9000, extras_z: 6000,
    notes: "12th Bipartite Settlement basic. Special allowance + city compensatory + leased accommodation in metros.",
  },
  {
    post_slug: "ibps-clerk",
    sector: "Banking",
    post: "IBPS Clerk",
    level: 0,
    basic: 24050,
    ta_lower: 0, ta_upper: 0,
    extras_x: 8000, extras_y: 6000, extras_z: 4500,
    notes: "12th Bipartite Settlement clerical scale. Multi-allowances vary by bank.",
  },
  {
    post_slug: "sbi-po",
    sector: "Banking",
    post: "SBI PO",
    level: 0,
    basic: 41960,
    ta_lower: 0, ta_upper: 0,
    extras_x: 14000, extras_y: 11000, extras_z: 8000,
    notes: "SBI's revised basic is ~17% higher than other PSBs. Includes Bank's own special allowance (no IBA agreement applies).",
  },
  {
    post_slug: "rbi-grade-b",
    sector: "Banking",
    post: "RBI Grade B Officer",
    level: 0,
    basic: 55200,
    ta_lower: 0, ta_upper: 0,
    extras_x: 22000, extras_y: 18000, extras_z: 14000,
    notes: "Premium scale. Includes special allowance, grade allowance, learning allowance. Total CTC > Rs. 18 lakh/yr.",
  },

  // ---------- UPSC ----------
  {
    post_slug: "upsc-ias",
    sector: "UPSC",
    post: "IAS Officer (Junior Time Scale)",
    level: 10,
    basic: 56100,
    ta_lower: 5400, ta_upper: 7200,
    extras_x: 0, extras_y: 0, extras_z: 0,
    notes: "Level-10. Sub-Divisional Magistrate as the first posting. District allowance + Bungalow + Vehicle.",
  },
  {
    post_slug: "upsc-ips",
    sector: "UPSC",
    post: "IPS Officer (ASP)",
    level: 10,
    basic: 56100,
    ta_lower: 5400, ta_upper: 7200,
    extras_x: 0, extras_y: 0, extras_z: 0,
    notes: "Level-10. ASP posting at Senior SP unit. Risk + ration + uniform + driver/orderly perks.",
  },

  // ---------- Teaching ----------
  {
    post_slug: "kvs-prt",
    sector: "Teaching",
    post: "KVS PRT (Primary Teacher)",
    level: 6,
    basic: 35400,
    ta_lower: 3600, ta_upper: 3600,
    extras_x: 0, extras_y: 0, extras_z: 0,
    notes: "Level-6. Promotion to TGT in 4-6 yrs (after meeting eligibility).",
  },
  {
    post_slug: "kvs-tgt",
    sector: "Teaching",
    post: "KVS TGT (Trained Graduate Teacher)",
    level: 7,
    basic: 44900,
    ta_lower: 3600, ta_upper: 5400,
    extras_x: 0, extras_y: 0, extras_z: 0,
    notes: "Level-7. Promotion to PGT possible after specialisation.",
  },

  // ---------- Defense ----------
  {
    post_slug: "nda",
    sector: "Defense",
    post: "Lieutenant (Army / Navy / Air Force) — after NDA + IMA",
    level: 10,
    basic: 56100,
    ta_lower: 5400, ta_upper: 7200,
    extras_x: 15500, extras_y: 15500, extras_z: 15500,
    notes: "MSP (Military Service Pay) Rs. 15,500 + Kit Maint + Field/Counter-Insurgency allowance varies. Rs. 1+ lakh CTC at field posting.",
  },
];
